import type { SubmitIntegrationRequestInput } from "../lib/api";
import type { ImportRejection } from "../lib/imports";
import type {
  DoneForYouSetupRequest,
  InvoiceRecord,
  Order,
  OrderItem,
  Product,
  QuoteRequest,
  Role,
  SupplierOrderRequest,
} from "../types";

export const LOCAL_CATALOG_PRODUCTS_KEY = "veleo.localCatalogProducts";
export const LOCAL_CONNECTOR_REQUESTS_KEY = "veleo.localConnectorRequests";
export const LOCAL_DONE_FOR_YOU_SETUP_REQUESTS_KEY = "veleo.localDoneForYouSetupRequests";
export const LOCAL_IMPORT_RUNS_KEY = "veleo.localImportRuns";
export const LOCAL_INVOICES_KEY = "veleo.localInvoices";
export const LOCAL_ORDER_GUIDE_KEY = "veleo.localOrderGuideItems";
export const LOCAL_ORDERS_KEY = "veleo.localOrders";
export const LOCAL_QUOTE_REQUESTS_KEY = "veleo.localQuoteRequests";
export const LOCAL_SUPPLIER_ORDER_REQUESTS_KEY = "veleo.localSupplierOrderRequests";

export const ACCOUNT_BUSINESS_NAME_KEY = "veleo.accountBusinessName";
export const ACCOUNT_EMAIL_KEY = "veleo.accountEmail";
export const PENDING_SIGNUP_KEY = "veleo.pendingSignup";
export const PERMISSION_ROLE_KEY = "veleo.permissionRoleId";
export const PERMISSION_TOUCHED_KEY = "veleo.permissionRoleTouched";
export const REAL_RESTAURANT_DATA_KEY = "veleo.realRestaurantDataSeen";
export const REAL_SUPPLIER_DATA_KEY = "veleo.realSupplierDataSeen";

export const ALLOW_LOCAL_WORKSPACE_FALLBACK = import.meta.env.DEV;

export type ImportRunRecord = {
  id: string;
  batchId: string;
  fileName: string;
  importType: "supplier_catalog" | "restaurant_order_guide";
  organizationName: string;
  role: Role;
  mode: "supabase" | "local";
  rowCount: number;
  acceptedCount: number;
  rejectedCount: number;
  rejectedRows: ImportRejection[];
  createdAt: string;
};

export type ConnectorRequestRecord = SubmitIntegrationRequestInput & {
  id: string;
  requestId: string;
  mode: "supabase" | "local";
  status: "pending_setup" | "connected" | "failed";
  statusNote?: string;
  createdAt: string;
  updatedAt?: string;
};

export function storageGet(key: string) {
  if (typeof window === "undefined") {
    return null;
  }

  return window.localStorage.getItem(key);
}

export function storageSet(key: string, value: string) {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(key, value);
  }
}

export function storageRemove(key: string) {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(key);
  }
}

export function readStoredJson<T>(key: string, fallback: T): T {
  const stored = storageGet(key);

  if (!stored) {
    return fallback;
  }

  try {
    return JSON.parse(stored) as T;
  } catch {
    return fallback;
  }
}

export function readLocalOrders() {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return [];
  }

  return readStoredJson<Order[]>(LOCAL_ORDERS_KEY, []);
}

export function saveLocalOrders(orders: Order[]) {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return;
  }

  storageSet(LOCAL_ORDERS_KEY, JSON.stringify(orders.slice(0, 100)));
}

export function isLocalOrderId(orderId: string) {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return false;
  }

  return orderId.startsWith("LO-") || readLocalOrders().some((order) => order.id === orderId);
}

export function readLocalCatalogProducts() {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return [];
  }

  return readStoredJson<Product[]>(LOCAL_CATALOG_PRODUCTS_KEY, []);
}

export function saveLocalCatalogProducts(products: Product[]) {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return;
  }

  storageSet(LOCAL_CATALOG_PRODUCTS_KEY, JSON.stringify(products.slice(0, 500)));
}

export function readLocalOrderGuideItems() {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return [];
  }

  return readStoredJson<OrderItem[]>(LOCAL_ORDER_GUIDE_KEY, []);
}

export function saveLocalOrderGuideItems(items: OrderItem[]) {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return;
  }

  storageSet(LOCAL_ORDER_GUIDE_KEY, JSON.stringify(items.slice(0, 500)));
}

export function readLocalImportRuns() {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return [];
  }

  return readStoredJson<ImportRunRecord[]>(LOCAL_IMPORT_RUNS_KEY, []);
}

export function saveLocalImportRuns(runs: ImportRunRecord[]) {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return;
  }

  storageSet(LOCAL_IMPORT_RUNS_KEY, JSON.stringify(runs.slice(0, 100)));
}

export function readLocalConnectorRequests() {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return [];
  }

  return readStoredJson<ConnectorRequestRecord[]>(LOCAL_CONNECTOR_REQUESTS_KEY, []);
}

export function saveLocalConnectorRequests(requests: ConnectorRequestRecord[]) {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return;
  }

  storageSet(LOCAL_CONNECTOR_REQUESTS_KEY, JSON.stringify(requests.slice(0, 100)));
}

export function readLocalDoneForYouSetupRequests() {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return [];
  }

  return readStoredJson<DoneForYouSetupRequest[]>(LOCAL_DONE_FOR_YOU_SETUP_REQUESTS_KEY, []);
}

export function saveLocalDoneForYouSetupRequests(requests: DoneForYouSetupRequest[]) {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return;
  }

  storageSet(LOCAL_DONE_FOR_YOU_SETUP_REQUESTS_KEY, JSON.stringify(requests.slice(0, 100)));
}

export function readLocalQuoteRequests() {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return [];
  }

  return readStoredJson<QuoteRequest[]>(LOCAL_QUOTE_REQUESTS_KEY, []);
}

export function saveLocalQuoteRequests(requests: QuoteRequest[]) {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return;
  }

  storageSet(LOCAL_QUOTE_REQUESTS_KEY, JSON.stringify(requests.slice(0, 100)));
}

export function readLocalSupplierOrderRequests() {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return [];
  }

  return readStoredJson<SupplierOrderRequest[]>(LOCAL_SUPPLIER_ORDER_REQUESTS_KEY, []);
}

export function saveLocalSupplierOrderRequests(requests: SupplierOrderRequest[]) {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return;
  }

  storageSet(LOCAL_SUPPLIER_ORDER_REQUESTS_KEY, JSON.stringify(requests.slice(0, 200)));
}

export function readLocalInvoices() {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return [];
  }

  return readStoredJson<InvoiceRecord[]>(LOCAL_INVOICES_KEY, []);
}

export function saveLocalInvoices(invoices: InvoiceRecord[]) {
  if (!ALLOW_LOCAL_WORKSPACE_FALLBACK) {
    return;
  }

  storageSet(LOCAL_INVOICES_KEY, JSON.stringify(invoices.slice(0, 200)));
}
